import type { CompanyStatus } from '@l2u/ui/lib/types.ts';
import type { StatusFilter } from '../lib/companyFilter.ts';
import { COMPANY_STATUSES, COMPANY_STATUS_LABELS } from '@l2u/ui/lib/companyStatus.ts';

type Props = {
  value: StatusFilter;
  counts: Record<CompanyStatus, number>;
  total: number;
  onChange: (value: StatusFilter) => void;
};

/**
 * Pill-style status filter for the company list. Semantically a *filter* — a row
 * of toggle buttons that reflect their state via aria-pressed — NOT tab/tabpanel
 * navigation: one shared table is filtered rather than panels being switched, so
 * the WAI-ARIA tabs pattern (used by components/Tabs.tsx) would be the wrong
 * semantics here. The active pill is tinted from the tenant's --brand-accent so
 * it stays on-brand under white-labelling instead of a hard-coded colour.
 */
export function StatusFilterTabs({ value, counts, total, onChange }: Props) {
  const items: { key: StatusFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: total },
    ...COMPANY_STATUSES.map((s) => ({
      key: s,
      label: COMPANY_STATUS_LABELS[s],
      count: counts[s],
    })),
  ];

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
      {items.map((item) => {
        const isActive = item.key === value;
        return (
          <button
            key={item.key}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(item.key)}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition ${
              isActive
                ? 'font-semibold'
                : 'border-slate-200 bg-white font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800'
            }`}
            style={
              isActive
                ? {
                    color: 'var(--brand-accent)',
                    borderColor: 'var(--brand-accent)',
                    backgroundColor: 'color-mix(in srgb, var(--brand-accent) 8%, white)',
                  }
                : undefined
            }
          >
            {item.label}
            <span
              className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${
                isActive ? '' : 'bg-slate-100 text-slate-600'
              }`}
              style={
                isActive
                  ? {
                      color: 'var(--brand-accent)',
                      backgroundColor: 'color-mix(in srgb, var(--brand-accent) 16%, white)',
                    }
                  : undefined
              }
            >
              {item.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
